import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { NewInput, CheckBox, CheckStyles, NewSelect } from "../..";
import { DataObj, getLabelFromKey, NewInputHandles } from "../../../Definitions";
import { getDataLevelFromKey, ReportTemplate, SelectFieldOption_Values } from "../../../Definitions/valueTypes";
import { ReportModalTypes, useReportContext } from "../_Reports/reportModalContext";
import { cloneDeep } from "lodash";

export default function ReportModalLvl1({templates}:{templates:ReportTemplate[]}) {
  const { t } = useTranslation()
  const headerRef = useRef<NewInputHandles | null>(null)
  const { state: { report, template, currentViewType }, dispatch } = useReportContext();
  const mobileXs = useMediaQuery(`(max-width: 793px)`);
  const [copyTemplateIsHovered, setCopyTemplateIsHovered] = useState<boolean>(false);
  const _reportModal = currentViewType === ReportModalTypes.createReport
  const _templateModal = currentViewType === ReportModalTypes.createTemplate || currentViewType === ReportModalTypes.copyTemplate

  const templateOptions = useMemo(() => {
    const options = templates.map(template => ({
      label: template.name,
      id: template.templateId,
    })) as SelectFieldOption_Values[];
  
    return options;
  }, [templates]);

  // Data Levels
 const dataLevels = useMemo(() => {
    const _dataLevels: DataObj[] = [
      "animal_number",
      "animal_group_id", 
      "system_id"
    ]

    return _dataLevels.map((param, index) => {
      const label = getLabelFromKey(param);
      
      return (
        <CheckBox 
          key={index} 
          text={t(`params.${label}`, {defaultValue: `${label}`})}
          checkStyle={CheckStyles.blue} 
          isChecked={_templateModal ? template?.dataLevel === getDataLevelFromKey(param) : report?.dataLevel === getDataLevelFromKey(param)}
          onChange={() => {_templateModal ? dispatch({template: {dataLevel: getDataLevelFromKey(param)}}) : dispatch({report: {dataLevel: getDataLevelFromKey(param)}})}}
          disabled={currentViewType === ReportModalTypes.copyTemplate}
        />
      )
    })
  }, [_templateModal, currentViewType, dispatch, report?.dataLevel, t, template?.dataLevel])

  return (
    <div style={{ width: "100%", display: "grid", gridTemplateRows: mobileXs ? "2px 1fr 2px 120px 2px" : "1fr 2px 76px 2px", gap: "5px", gridTemplateColumns: "1fr", height: "100%" }}>

      {mobileXs && <div style={{ gridArea: "1/1/2/2", borderBottom: "2px solid "+colors.blueDarkSecondary }}/>}    
        <div style={{gridArea: !mobileXs ? "1/1/2/2" : "2/1/3/2", gap: mobileXs ? "20px" : undefined, padding: "10px", height: "100%", maxHeight: "40vh", boxSizing: "border-box", overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary+colors.transparent, display: "grid", gridTemplateColumns: mobileXs ? "1fr" : "350px 1fr", gridTemplateRows: "1fr" }}>
                
        <div style={{ gridArea: "1/1/2/2", display: "flex", flexDirection: mobileXs ? "column" : "row", gap: "15px", alignItems: mobileXs ? "center" : undefined }}>  
        <NewInput
          ref={headerRef}
          header={ _templateModal ? t(t("reportPages.reports.templateName", {defaultValue: "Template Name"})) : t("reportPages.reports.reportName", {defaultValue: "Report Name"})}
          onChange={(value) => { _templateModal ? dispatch({ template: {name: value}}) : dispatch({ report: {name: value} })} }
          isRequired
          valueOverrides={{
            defaultValue: _templateModal ? template.name : _reportModal ? report.name : ""
          }}
          isClearable
        />
        {_reportModal ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <NewInput
              header={t("reportPages.reports.startDate", {defaultValue: "start date"})}
              valueOverrides={{
                valueType: 'date',
                defaultValue: report?.startDate ? report.startDate : undefined
              }}
              onChange={(e) => dispatch({report: {startDate: new Date(e).getTime()}})}
              startEnhancer={glyphs.Calendar({})}
              isRequired
              isClearable
            />
            <NewInput
              header={t("reportPages.reports.endDate", {defaultValue: "end date"})}
              valueOverrides={{
                valueType: 'date',
                defaultValue: report?.endDate ? report.endDate : undefined
              }}
              onChange={(e) => dispatch({report: {endDate: new Date(e).getTime()}})}
              startEnhancer={glyphs.Calendar({})}
              isRequired
              isClearable
            />
          </div>
        ) : ( 
            <div
              onMouseEnter={() => setCopyTemplateIsHovered(true)}
              onMouseLeave={() => setCopyTemplateIsHovered(false)}
              style={{ filter: currentViewType === ReportModalTypes.copyTemplate || copyTemplateIsHovered === true? "opacity(1)" : "opacity(0.5)", transition: "filter 0.3s ease", position: "relative", width: "fit-content", height: "fit-content" }}
            >
            <NewSelect
              header={t("widgets.general.copyExisting", { defaultValue: "copy existing" })}
              isClearable
              options={templateOptions ?? []}
              onChange={(e) => {
                if (!e) {
                  headerRef.current?.setValue("");
                  dispatch({
                    reset: true,
                    currentViewType: ReportModalTypes.createTemplate,
                  })
                  return
                }
            
                const selected = templates.find(t => t.templateId === e.id); 
                dispatch({ template: cloneDeep(selected), currentViewType: ReportModalTypes.copyTemplate });
              }}
                valueOverrides={{
                defaultValue: _templateModal && template !== undefined
                  ? templateOptions.find(t => t.id === template.templateId)
                  : undefined
                }}
            />
          </div>
        )}
        </div>
      </div>
      
      <div style={{ gridArea: !mobileXs ? "2/1/3/2" : "3/1/4/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />
      
      <div style={{ gridArea: !mobileXs ? "3/1/4/2" : "4/1/5/2", padding: "10px", boxSizing: "border-box", display: "flex", alignItems: "flex-start", justifyContent: "center", flexDirection: "column", gap: "10px", overflow: mobileXs ? "auto" : "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary+colors.transparent }}>
        <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 0px 10px" }}>{t("reportPages.reports.dataLevel", { defaultValue: "data level" })}</p>
        <div style={{width: "fit-content", display: "flex", flexWrap: "wrap", columnGap: mobileXs ? "10px" : "60px", rowGap: "10px", margin: "0 auto", justifyContent: "center"}}>
          {dataLevels}
        </div>  
      </div>
      <div style={{ gridArea: !mobileXs ? "4/1/5/2" : "5/1/6/2", borderBottom: "2px solid "+colors.blueDarkSecondary }}/>
      
    </div>
  )
}